import {useIntl} from 'react-intl';
import {Box, BoxProps, ComponentWithAs, Container, Flex, IconProps, Text} from '@chakra-ui/react';
import dayjs from 'dayjs';

import {useTranslate} from '@eksab/i18n';
import {CalendarIcon, CoinsIcon2 as CoinsIcon, TShirtIcon} from '@eksab/assets/icons';
import {FC} from 'react';
import {useLineupStore} from './useLineupStore';

import {shallow} from 'zustand/shallow';

interface LineupStatsProps extends BoxProps {
  start: string;
  budget: string | undefined;
}

export function LineupStats({start, budget, ...props}: LineupStatsProps) {
  const format = useIntl().formatNumber;

  const t = useTranslate();

  const {numberOfPlayer, remaining} = useLineupStore(
    (state) => ({
      numberOfPlayer: state.numberOfPlayers,
      remaining: (budget ? Number(budget) : 0) - state.overallCost,
    }),
    shallow,
  );

  return (
    <Box bg="white" h={['3.75rem', 20, null, '6.25rem']} shadow="xs" pos="sticky" top="0" zIndex={100} {...props}>
      <Container w={{base: 'full', xl: '85%'}} h="full" gap="7" justifyContent="space-between" alignItems="center">
        <StatsItem>
          <StatsItem.Icon icon={CoinsIcon} />
          <StatsItem.Content color={+remaining < 0 ? 'red.600' : 'black'}>
            {t('fantasy.lineup.price-unit', {
              amount: format(remaining, {minimumFractionDigits: 1}),
            })}
          </StatsItem.Content>
        </StatsItem>

        <StatsItem>
          <StatsItem.Icon icon={TShirtIcon} />
          <StatsItem.Content textTransform="capitalize">
            {format(numberOfPlayer)} / {format(15)} {t('fantasy.lineup.player')}
          </StatsItem.Content>
        </StatsItem>

        <StatsItem>
          <StatsItem.Icon icon={CalendarIcon} />
          <StatsItem.Content>{dayjs(start).fromNow()}</StatsItem.Content>
        </StatsItem>
      </Container>
    </Box>
  );
}

interface StatsItemProps extends FC<BoxProps> {
  Icon: FC<{icon: ComponentWithAs<'svg', IconProps>}>;
  Content: FC<BoxProps>;
}

const StatsItem: StatsItemProps = ({children, ...props}) => (
  <Flex gap={[2, null, 3.5]} alignItems="center" {...props}>
    {children}
  </Flex>
);

const StatsItemIcon: StatsItemProps['Icon'] = ({icon: Icon}) => (
  <Icon width="auto" height="clamp(1.25rem, 5vw, 2.6rem)" />
);

const StatsItemContent: StatsItemProps['Content'] = ({children, ...props}) => (
  <Text fontSize={['sm', 'md', 'xl']} fontWeight="800" as="span" {...props}>
    {children}
  </Text>
);

StatsItem.Icon = StatsItemIcon;
StatsItem.Content = StatsItemContent;
